import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.ts';
import { Diary } from '../admin/Diary.tsx';
import { HoursEditor } from '../admin/HoursEditor.tsx';
import { BlackoutsEditor } from '../admin/BlackoutsEditor.tsx';
import { TreatmentsPanel } from '../admin/TreatmentsPanel.tsx';
import { SettingsEditor } from '../admin/SettingsEditor.tsx';
import { ContentEditor } from '../admin/ContentEditor.tsx';
import { ConsultationsPanel } from '../admin/ConsultationsPanel.tsx';
import './admin.css';

const TOKEN_KEY = 'renewal-admin-token';

type Tab = 'diary' | 'consultations' | 'hours' | 'closures' | 'treatments' | 'content' | 'rules';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'diary', label: 'Diary' },
  { id: 'consultations', label: 'Consultations' },
  { id: 'hours', label: 'Opening hours' },
  { id: 'closures', label: 'Closures' },
  { id: 'treatments', label: 'Treatments' },
  { id: 'content', label: 'Site wording' },
  { id: 'rules', label: 'Booking rules' },
];

/**
 * Session storage rather than local storage: the token dies with the browser
 * session, so a shared or public machine does not leave the diary open.
 * Wrapped because storage throws outright in some privacy modes.
 */
function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // A session that cannot be persisted still works for this page load.
  }
}

export function Admin() {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [checking, setChecking] = useState(Boolean(readStoredToken()));
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>('diary');

  // Admin keeps the original light palette; the public pages are the dark
  // evening theme. A layout effect, so the first paint is already light.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = 'light';
    return () => {
      delete root.dataset.theme;
    };
  }, []);

  // A stored token may have expired since the tab was last open.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    api
      .admin.checkSession(token)
      .then(() => {
        if (!cancelled) setChecking(false);
      })
      .catch(() => {
        if (cancelled) return;
        storeToken(null);
        setToken(null);
        setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const signOut = useCallback(() => {
    storeToken(null);
    setToken(null);
    setPassword('');
  }, []);

  /** Any 401 from a child panel means the session has gone; drop straight out. */
  const onUnauthorised = useCallback(() => signOut(), [signOut]);

  async function signIn(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setLoginError(null);
    try {
      const result = await api.admin.login(password);
      storeToken(result.token);
      setToken(result.token);
      setChecking(false);
      setPassword('');
    } catch (caught) {
      setLoginError(caught instanceof ApiError ? caught.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="admin-login">
        <form className="panel admin-login__panel stack-lg" onSubmit={signIn}>
          <div>
            <p className="eyebrow">The Renewal Studio</p>
            <h1 className="admin-login__title">Studio login</h1>
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              className="input"
              value={password}
              autoComplete="current-password"
              autoFocus
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {loginError && <p className="notice notice--error">{loginError}</p>}

          <button type="submit" className="btn btn--block" disabled={busy || !password}>
            {busy ? <span className="spinner" /> : 'Sign in'}
          </button>

          <Link to="/" className="admin-login__back">
            Back to the website
          </Link>
        </form>
      </main>
    );
  }

  if (checking) {
    return (
      <main className="admin-login">
        <p className="booking__loading">
          <span className="spinner" /> Checking your session…
        </p>
      </main>
    );
  }

  return (
    <div className="admin">
      <header className="admin__header">
        <div className="admin__header-inner">
          <div>
            <p className="eyebrow admin__eyebrow">The Renewal Studio</p>
            <h1 className="admin__title">Studio admin</h1>
          </div>
          <div className="row">
            <Link to="/" className="btn btn--quiet">
              View website
            </Link>
            <button type="button" className="btn btn--ghost admin__signout" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>

        <nav className="admin__tabs" aria-label="Admin sections">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`admin__tab ${tab === entry.id ? 'admin__tab--active' : ''}`}
              aria-current={tab === entry.id ? 'page' : undefined}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="admin__body">
        {tab === 'diary' && <Diary token={token} onUnauthorised={onUnauthorised} />}
        {tab === 'consultations' && (
          <ConsultationsPanel token={token} onUnauthorised={onUnauthorised} />
        )}
        {tab === 'hours' && <HoursEditor token={token} onUnauthorised={onUnauthorised} />}
        {tab === 'closures' && <BlackoutsEditor token={token} onUnauthorised={onUnauthorised} />}
        {tab === 'treatments' && <TreatmentsPanel token={token} onUnauthorised={onUnauthorised} />}
        {tab === 'content' && <ContentEditor token={token} onUnauthorised={onUnauthorised} />}
        {tab === 'rules' && <SettingsEditor token={token} onUnauthorised={onUnauthorised} />}
      </main>
    </div>
  );
}
